// The standalone SVG of a saved single-line diagram (SLD-a, Master Prompt A26 (j)): the
// picture the editor draws, a legend, the GAP-023 note and a title block, in one file that
// opens anywhere. Only a saved version is exported (traceable: version, content hash, who
// saved it and when); the caller says whether the draft on screen differs from it.
//
// Colours: the file cannot read the app's design tokens, so they are resolved at export
// time from the live page (getComputedStyle on :root) into plain values written into the
// file's own <style>; nothing here names a colour literal, and the file holds no var().
// Marks (a structural error, a bus no source energises) also carry a stroke pattern, so
// they survive a black-and-white print.

export type ExportColours = {
  text: string;
  muted: string;
  surface: string;
  brand: string;
  onBrand: string;
  fail: string;
  warning: string;
  border: string;
};

/** The design token each export colour comes from, and its named fallback (no page: tests). */
const COLOUR_TOKENS: Record<keyof ExportColours, { token: string; fallback: string }> = {
  text: { token: "--kes-color-text", fallback: "black" },
  muted: { token: "--kes-color-muted", fallback: "dimgray" },
  surface: { token: "--kes-color-surface", fallback: "white" },
  brand: { token: "--kes-color-brand", fallback: "navy" },
  onBrand: { token: "--kes-color-on-brand", fallback: "white" },
  fail: { token: "--kes-color-fail", fallback: "firebrick" },
  warning: { token: "--kes-color-warning", fallback: "darkgoldenrod" },
  border: { token: "--kes-color-border-subtle", fallback: "silver" },
};

/** The export colours as the live page defines them; the named fallbacks where it does not. */
export function resolveExportColours(root: Element | null = null): ExportColours {
  const style =
    root !== null && typeof getComputedStyle === "function" ? getComputedStyle(root) : null;
  const resolved = {} as ExportColours;
  for (const [key, { token, fallback }] of Object.entries(COLOUR_TOKENS)) {
    const value = style?.getPropertyValue(token).trim() ?? "";
    resolved[key as keyof ExportColours] = value !== "" ? value : fallback;
  }
  return resolved;
}

export const EXPORT_FONT = '"IBM Plex Sans", "Segoe UI", system-ui, Arial, sans-serif';

/** The stylesheet of the file: the diagram rules of styles/sld.css with the colours written in. */
export function exportStyle(colours: ExportColours): string {
  return [
    `text { font-family: ${EXPORT_FONT}; font-size: 12px; fill: ${colours.text}; }`,
    `text[data-sld-label] { fill: ${colours.muted}; }`,
    `text[data-sld-symbol-text] { font-weight: 700; text-anchor: middle; dominant-baseline: central; }`,
    `polyline { fill: none; stroke: ${colours.text}; stroke-width: 2; stroke-linejoin: round; }`,
    `[data-sld-kind="cable"] polyline { stroke-dasharray: 6 4; }`,
    `[data-sld-kind="device"] rect, [data-sld-kind="transformer"] circle, [data-sld-kind="source"] circle, ` +
      `[data-sld-kind="load"] circle, [data-sld-kind="load"] rect ` +
      `{ fill: ${colours.surface}; stroke: ${colours.text}; stroke-width: 2; }`,
    `[data-sld-kind="bus"] rect { fill: ${colours.brand}; stroke: ${colours.brand}; stroke-width: 2; }`,
    `[data-sld-kind="bus"] text[data-sld-symbol-text] { fill: ${colours.onBrand}; }`,
    // Marks: colour and a pattern, so they read in black and white too.
    `[data-sld-mark="error"] rect, [data-sld-mark="error"] circle, [data-sld-mark="error"] polyline ` +
      `{ stroke: ${colours.fail}; stroke-width: 3; stroke-dasharray: 2 3; }`,
    `[data-sld-mark="error"] text[data-sld-label] { fill: ${colours.fail}; font-weight: 700; }`,
    `[data-sld-mark="warning"] rect, [data-sld-mark="warning"] circle, [data-sld-mark="warning"] polyline ` +
      `{ stroke: ${colours.warning}; stroke-width: 3; stroke-dasharray: 9 3 2 3; }`,
    `[data-sld-mark="warning"] text[data-sld-label] { fill: ${colours.warning}; font-weight: 700; }`,
    `text[data-sld-unconnected-title] { fill: ${colours.warning}; font-weight: 700; }`,
    `.title-block rect { fill: none; stroke: ${colours.text}; stroke-width: 1; }`,
    `.title-block text { fill: ${colours.text}; }`,
    `.title-block text.key { fill: ${colours.muted}; }`,
    `.title-block text.title { font-size: 16px; font-weight: 700; }`,
    `.legend text { fill: ${colours.muted}; }`,
    `.legend .cable { stroke-dasharray: 6 4; }`,
    `.frame { fill: ${colours.surface}; stroke: ${colours.border}; stroke-width: 1; }`,
  ].join("\n");
}

export type TitleBlockFacts = {
  projectCode: string;
  projectName: string;
  revisionLabel: string;
  revisionStatus: string;
  sldCode: string;
  sldName: string;
  version: number;
  contentHash: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  /** When the file was made. */
  generatedAt: Date;
};

/** "2026-09-30 05:12 UTC" from an ISO string or a Date; "" for nothing; the text for a bad value. */
export function formatStamp(value: string | Date | null): string {
  if (value === null || value === "") {
    return "";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return `${date.toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

/** The rows of the title block, key and value, in reading order. */
export function titleBlockRows(facts: TitleBlockFacts): [string, string][] {
  return [
    ["Project", `${facts.projectCode} — ${facts.projectName}`],
    ["Revision", `${facts.revisionLabel} (${facts.revisionStatus})`],
    ["Diagram", `${facts.sldCode} — ${facts.sldName}`],
    ["Saved version", String(facts.version)],
    ["Content hash", facts.contentHash === null ? "—" : facts.contentHash.slice(0, 12)],
    [
      "Updated",
      [facts.updatedBy ?? "", formatStamp(facts.updatedAt)].filter(Boolean).join(", ") || "—",
    ],
    ["Generated", formatStamp(facts.generatedAt)],
  ];
}

/** "<project code>-rev<revision number>-SLD-v<version>.svg", the code made file-safe. */
export function sldExportFileName(
  projectCode: string,
  revisionNumber: number,
  version: number,
): string {
  const safe = projectCode
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9._-]/g, "");
  return `${safe}-rev${revisionNumber}-SLD-v${version}.svg`;
}

export function escapeXml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export const GENERIC_SHAPES_NOTE = "Generic shapes, not IEC 60617 symbols (GAP-023).";
export const LEGEND_ITEMS = [
  "Bus",
  "Device",
  "Cable",
  "Transformer",
  "Source (~ grid, G generator, U UPS, PV)",
  "Motor (M) / load",
] as const;

const MARGIN = 24;
const LEGEND_HEIGHT = 40;
const ROW_HEIGHT = 18;
const BLOCK_PAD = 10;
const MIN_WIDTH = 760;

export type SldSvgInput = {
  /** The picture as the editor renders it: one <svg> element with width, height and viewBox. */
  pictureSvg: string;
  pictureWidth: number;
  pictureHeight: number;
  facts: TitleBlockFacts;
  colours: ExportColours;
};

/**
 * The standalone file: the picture at the top, the legend and the note under it, the
 * title block at the bottom, all inside one frame. The picture keeps its own coordinate
 * system as a nested <svg>.
 */
export function buildSldSvg(input: SldSvgInput): string {
  const rows = titleBlockRows(input.facts);
  const half = Math.ceil(rows.length / 2);
  const blockHeight = BLOCK_PAD * 2 + ROW_HEIGHT * (half + 1);
  const width = Math.max(MIN_WIDTH, input.pictureWidth) + MARGIN * 2;
  const legendTop = MARGIN + input.pictureHeight + 8;
  const blockTop = legendTop + LEGEND_HEIGHT + 8;
  const height = blockTop + blockHeight + MARGIN;

  const legend = LEGEND_ITEMS.map((item, index) => {
    const x = MARGIN + index * 124;
    const y = legendTop + 14;
    const symbol =
      index === 0
        ? `<rect x="${x}" y="${y - 4}" width="24" height="8" rx="1" data-sld-kind="bus"/>`
        : index === 1
          ? `<g data-sld-kind="device"><polyline points="${x},${y} ${x + 7},${y}"/><rect x="${x + 7}" y="${y - 5}" width="10" height="10"/><polyline points="${x + 17},${y} ${x + 24},${y}"/></g>`
          : index === 2
            ? `<polyline class="cable" points="${x},${y} ${x + 24},${y}"/>`
            : index === 3
              ? `<g data-sld-kind="transformer"><circle cx="${x + 12}" cy="${y - 4}" r="5"/><circle cx="${x + 12}" cy="${y + 4}" r="5"/></g>`
              : `<g data-sld-kind="${index === 4 ? "source" : "load"}"><circle cx="${x + 12}" cy="${y}" r="9"/></g>`;
    return `<g class="legend-item">${symbol}<text x="${x + 30}" y="${y + 4}">${escapeXml(item)}</text></g>`;
  }).join("");
  // A bus rect in the legend takes the bus rule through its own attribute.
  const legendBlock = `<g class="legend">${legend.replace(
    /<rect ([^>]*) data-sld-kind="bus"\/>/,
    (_match, attributes: string) => `<g data-sld-kind="bus"><rect ${attributes}/></g>`,
  )}<text x="${MARGIN}" y="${legendTop + LEGEND_HEIGHT - 4}">${escapeXml(GENERIC_SHAPES_NOTE)}</text></g>`;

  const columnWidth = (width - MARGIN * 2) / 2;
  const cells = rows
    .map(([key, value], index) => {
      const column = index < half ? 0 : 1;
      const row = index < half ? index : index - half;
      const x = MARGIN + BLOCK_PAD + column * columnWidth;
      const y = blockTop + BLOCK_PAD + ROW_HEIGHT * (row + 2) - 4;
      return (
        `<text class="key" x="${x}" y="${y}">${escapeXml(key)}</text>` +
        `<text x="${x + 110}" y="${y}">${escapeXml(value)}</text>`
      );
    })
    .join("");
  const titleBlock =
    `<g class="title-block">` +
    `<rect x="${MARGIN}" y="${blockTop}" width="${width - MARGIN * 2}" height="${blockHeight}"/>` +
    `<text class="title" x="${MARGIN + BLOCK_PAD}" y="${blockTop + BLOCK_PAD + 14}">Single-line diagram ${escapeXml(input.facts.sldCode)}</text>` +
    cells +
    `</g>`;

  const picture = input.pictureSvg.replace(/^<svg\b/, `<svg x="${MARGIN}" y="${MARGIN}"`);

  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" ` +
    `viewBox="0 0 ${width} ${height}" data-sld-export="true">\n` +
    `<title>Single-line diagram ${escapeXml(input.facts.sldCode)} — ${escapeXml(input.facts.projectCode)} ${escapeXml(input.facts.revisionLabel)} v${input.facts.version}</title>\n` +
    `<style>\n${exportStyle(input.colours)}\n</style>\n` +
    `<rect class="frame" x="0.5" y="0.5" width="${width - 1}" height="${height - 1}"/>\n` +
    `${picture}\n${legendBlock}\n${titleBlock}\n</svg>\n`
  );
}
